'use client';

// Shared genre dropdown — used by catalog, preorders, and any list with a
// `category` field. Single source of truth for the genre list + popover UI.

import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Sword, Crosshair, Shield, Gauge, Trophy, Flame, Cpu, Brain, HelpCircle, Gamepad2, Heart, Music, LayoutGrid } from 'lucide-react';
import { t } from '@/lib/i18n';

export const GENRES = [
  { key: 'Action & adventure', i18nKey: 'genre_action',      Icon: Sword      },
  { key: 'Shooter',            i18nKey: 'genre_shooter',     Icon: Crosshair  },
  { key: 'Role playing',       i18nKey: 'genre_rpg',         Icon: Shield     },
  { key: 'Racing & flying',    i18nKey: 'genre_racing',      Icon: Gauge      },
  { key: 'Sports',             i18nKey: 'genre_sports',      Icon: Trophy     },
  { key: 'Fighting',           i18nKey: 'genre_fighting',    Icon: Flame      },
  { key: 'Simulation',         i18nKey: 'genre_simulation',  Icon: Cpu        },
  { key: 'Strategy',           i18nKey: 'genre_strategy',    Icon: Brain      },
  { key: 'Puzzle & trivia',    i18nKey: 'genre_puzzle',      Icon: HelpCircle },
  { key: 'Platformer',         i18nKey: 'genre_platformer',  Icon: Gamepad2   },
  { key: 'Family & kids',      i18nKey: 'genre_family',      Icon: Heart      },
  { key: 'Music',              i18nKey: 'genre_music',       Icon: Music      },
] as const;

export function GenreFilter({ value, onChange, lang }: { value: string; onChange: (g: string) => void; lang: string }) {
  const [open, setOpen] = useState(false);
  const [pos,  setPos]  = useState<{ top: number; left: number } | null>(null);
  const btnRef  = useRef<HTMLButtonElement>(null);
  const dropRef = useRef<HTMLDivElement>(null);

  function updatePos() {
    if (btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setPos({ top: r.bottom + 6, left: r.left });
    }
  }

  useEffect(() => {
    if (!open) return;
    const onMouse = (e: MouseEvent) => {
      if (!dropRef.current?.contains(e.target as Node) &&
          !btnRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey    = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    const onScroll = () => setOpen(false);
    document.addEventListener('mousedown', onMouse);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      document.removeEventListener('mousedown', onMouse);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll);
    };
  }, [open]);

  function select(key: string) { onChange(key); setOpen(false); }

  const selected = GENRES.find(g => g.key === value);

  const popover = open && pos ? (
    <div
      ref={dropRef}
      role="listbox"
      aria-label={t('filter_aria_genre', lang)}
      className="gs-dropdown"
      style={{
        position: 'fixed', top: pos.top, left: pos.left,
        width: 420,
        zIndex: 9999,
        padding: 10,
        transformOrigin: 'top left',
      }}
    >
      <button
        role="option"
        aria-selected={value === ''}
        onClick={() => select('')}
        className={`genre-all-btn${value === '' ? ' genre-all-btn--active' : ''}`}
        style={{ cursor: 'pointer' }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <LayoutGrid size={15} strokeWidth={1.8} />
          <span style={{ fontSize: 13, fontWeight: 700 }}>{t('filter_all_genres', lang)}</span>
        </span>
        <span style={{ fontSize: 11, fontStyle: 'italic', color: 'var(--v4-fg-4)', letterSpacing: '.02em' }}>
          All genres
        </span>
      </button>

      <div style={{ height: 1, background: 'var(--v4-border)', margin: '8px 0' }} />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 5 }}>
        {GENRES.map(({ key, i18nKey, Icon }) => {
          const active = value === key;
          return (
            <button
              key={key}
              role="option"
              aria-selected={active}
              onClick={() => select(key)}
              className={`genre-tile${active ? ' genre-tile--active' : ''}`}
              style={{ cursor: 'pointer' }}
            >
              <Icon size={18} strokeWidth={1.7} className="genre-tile-icon" />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
                <span className="genre-tile-label">{t(i18nKey, lang)}</span>
                <span className="genre-tile-sub">{key}</span>
              </div>
            </button>
          );
        })}
      </div>

      <div style={{
        marginTop: 8, paddingTop: 7,
        borderTop: '1px solid var(--v4-border)',
        display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 5,
      }}>
        <kbd style={{
          fontSize: 10, padding: '2px 6px', lineHeight: 1.5,
          background: 'var(--v4-panel)', border: '1px solid var(--v4-border-strong)',
          borderRadius: 5, color: 'var(--v4-fg-3)', fontFamily: 'monospace',
        }}>Esc</kbd>
        <span style={{ fontSize: 10, color: 'var(--v4-fg-4)' }}>{t('filter_close', lang)}</span>
      </div>
    </div>
  ) : null;

  return (
    <div style={{ flexShrink: 0 }}>
      <button
        ref={btnRef}
        onClick={() => { if (!open) updatePos(); setOpen(o => !o); }}
        aria-expanded={open}
        aria-haspopup="listbox"
        className={`genre-trigger${value ? ' genre-trigger--active' : ''}${open ? ' genre-trigger--open' : ''}`}
        style={{ cursor: 'pointer' }}
      >
        {selected ? <selected.Icon size={11} strokeWidth={2} /> : <LayoutGrid size={11} strokeWidth={2} />}
        <span>{selected ? t(selected.i18nKey, lang) : t('filter_genre', lang)}</span>
        <ChevronDown size={10} className="genre-trigger-chevron" />
      </button>
      {typeof document !== 'undefined' && popover && createPortal(popover, document.body)}
    </div>
  );
}
